import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxSpaceBetween } from '@ecp/common/src/layouts/flex/styled';
import HighlightText from '@ecp/common/src/text/HighlightText';
import Input from '@ecp/common/src/components/input/bo/Input';
import { ReactComponent as EditIcon } from '@ecp/common/src/assets/icon/icon__bo__tree-edit.svg';
import { ReactComponent as DeleteIcon } from '@ecp/common/src/assets/icon/icon__bo__tree-delete.svg';

const StyledRoot = styled.div`
  max-width: 210px;
`;

const StyledTitle = styled.span`
  height: 28px;
  line-height: 28px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledEditButton = styled(EditIcon)`
  width: 14px;
  height: 14px;
`;

const StyledDeleteButton = styled(DeleteIcon)`
  width: 14px;
  height: 14px;
`;

const TreeNode = ({ editedKey, editable, onSave, onEdit, onDelete, hideHoverMenu = false, ...props }) => {
  const {
    title,
    data: { key },
    searchKeyword,
  } = props;

  const [hovered, setHovered] = useState(false);
  const editing = useMemo(() => editedKey === key, [editedKey, key]);

  const [inputValue, setInputValue] = useState(title);

  const handleMouseEnter = useCallback(() => setHovered(key !== 'ALL' && editable && true), [key, editable]);
  const handleMouseLeave = useCallback(() => setHovered(key !== 'ALL' && editable && false), [key, editable]);
  const handleChange = useCallback((e) => setInputValue(e.target.value), []);

  const handleSave = useCallback(
    (e) => {
      onSave && onSave(e, props);
      onEdit('');
    },
    [props, onSave, onEdit]
  );

  const handleEditClick = useCallback(() => {
    setInputValue(title);
    onEdit(key);
  }, [title, key, onEdit]);

  const handleDeleteClick = useCallback(
    (e) => {
      onDelete && onDelete(e, props);
    },
    [props, onDelete]
  );

  return (
    <StyledRoot onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {editable && (
        <Input
          width={'210px'}
          value={inputValue}
          onBlur={handleSave}
          onEnter={handleSave}
          onChange={handleChange}
          hidden={!editing}
          ref={(inputRef) => inputRef && inputRef.focus()}
        />
      )}
      {!editing && (
        <FlexBoxSpaceBetween
          height={'100%'}
          {...(hovered ? { width: '210px', padding: '0 12px', background: boTheme.color.sub8 } : {})}
        >
          <StyledTitle>
            <HighlightText keyword={searchKeyword}>{title}</HighlightText>
          </StyledTitle>
          {hovered && !hideHoverMenu && (
            <FlexBox gap={'12px'} style={{ marginLeft: '6px' }}>
              <StyledEditButton onClick={handleEditClick} />
              <StyledDeleteButton onClick={handleDeleteClick} />
            </FlexBox>
          )}
        </FlexBoxSpaceBetween>
      )}
    </StyledRoot>
  );
};

TreeNode.displayName = 'TreeNode';

TreeNode.propTypes = {
  title: PropTypes.string,
  key: PropTypes.string,
  data: PropTypes.object,
  editedKey: PropTypes.string,
  editable: PropTypes.bool,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  searchKeyword: PropTypes.string,
  hideHoverMenu: PropTypes.bool,
};

TreeNode.defaultProps = {
  rootComponent: 'div',
};

export default TreeNode;
